/**
 * Registry filters — the pure half of the global filter state (F-13/F-14).
 *
 * The state itself lives in the query string (STYLEGUIDE "URL-synced filters");
 * these helpers are unit-testable and framework-free. Every view reads the same
 * filters, so a status chosen on «Потоки» holds on «Исполнители» too.
 */

import { PROJECT_PRIORITIES, PROJECT_STATUSES, priorityRank } from '../api/types';
import type { Category, Priority, Project, ProjectStatus, RegistryStatus, Stream } from '../api/types';
import { isOverdue } from './capacity';
import { todayIso } from './date';

/** Query-string keys. Absent = unset (a cleared filter removes the key). */
export const FILTER_PARAM = {
  status: 'status',
  stream: 'stream',
  person: 'person',
  team: 'team',
  category: 'category',
  priority: 'priority',
  dueFrom: 'dueFrom',
  dueTo: 'dueTo',
  hasDue: 'hasDue',
  risk: 'risk',
  overdue: 'overdue',
  search: 'search',
} as const;

/**
 * One filter dimension = a LIST of values (owner: several statuses/streams/
 * people/teams/priorities selectable at once). A single value is a one-element
 * list — `?status=new` reads exactly like `?status=new,`-less legacy links.
 * Within a key the values are OR-ed, between keys AND-ed as before.
 */
export interface RegistryFilters {
  status?: ProjectStatus[];
  streamId?: string[];
  personId?: string[];
  teamId?: string[];
  categoryId?: string;
  priority?: Priority[];
  /** Period bounds on «Срок до» — ISO `YYYY-MM-DD`, inclusive on both ends. */
  dueFrom?: string;
  dueTo?: string;
  /** «Только со сроком» (F-28): only projects with a dueDate at all. */
  hasDueDate?: boolean;
  atRisk?: boolean;
  overdue?: boolean;
  search?: string;
}

export const EMPTY_FILTERS: RegistryFilters = {};

/** `a, b,,c` → ['a','b','c'] (trimmed, deduped); empty/noise → undefined. */
function asIdList(value: string | undefined): string[] | undefined {
  const items = (value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '');
  return items.length === 0 ? undefined : [...new Set(items)];
}

/** Same, but only the enum's own values survive (a bogus one is noise, not a filter). */
function asEnumList<T extends string>(value: string | undefined, allowed: readonly string[]): T[] | undefined {
  const items = asIdList(value)?.filter((item) => allowed.includes(item));
  return (items?.length ?? 0) === 0 ? undefined : (items as T[]);
}

const truthy = (value: string | undefined): boolean | undefined => {
  if (value === undefined || value === '' || value === '0' || value === 'false') return undefined;
  return true;
};

/** Only a well-formed ISO date is a filter — a typed-off value is noise. */
const asIsoDate = (value: string | undefined): string | undefined =>
  value !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;

/** `?status=&stream=&person=&team=&category=&priority=&risk=&overdue=&search=` → filters. */
export function readFilters(params: Record<string, string>): RegistryFilters {
  return {
    // Statuses are a managed dictionary (F-01, 2026-10-14): the parser cannot
    // know the valid ids, so any non-empty value is kept — a stale custom id
    // simply matches nothing (the filtered-empty state explains it), while a
    // valid one survives a refresh or a shared link.
    status: asIdList(params[FILTER_PARAM.status]),
    streamId: asIdList(params[FILTER_PARAM.stream]),
    personId: asIdList(params[FILTER_PARAM.person]),
    teamId: asIdList(params[FILTER_PARAM.team]),
    categoryId: params[FILTER_PARAM.category] || undefined,
    priority: asEnumList<Priority>(params[FILTER_PARAM.priority], PROJECT_PRIORITIES),
    dueFrom: asIsoDate(params[FILTER_PARAM.dueFrom]),
    dueTo: asIsoDate(params[FILTER_PARAM.dueTo]),
    hasDueDate: truthy(params[FILTER_PARAM.hasDue]),
    atRisk: truthy(params[FILTER_PARAM.risk]),
    overdue: truthy(params[FILTER_PARAM.overdue]),
    search: params[FILTER_PARAM.search]?.trim() || undefined,
  };
}

/** Filters → param patch (undefined/false drop the key, keeping the URL canonical). */
export function filtersToParams(filters: RegistryFilters): Record<string, string | number | boolean | null | undefined> {
  return {
    [FILTER_PARAM.status]: filters.status?.join(','),
    [FILTER_PARAM.stream]: filters.streamId?.join(','),
    [FILTER_PARAM.person]: filters.personId?.join(','),
    [FILTER_PARAM.team]: filters.teamId?.join(','),
    [FILTER_PARAM.category]: filters.categoryId,
    [FILTER_PARAM.priority]: filters.priority?.join(','),
    [FILTER_PARAM.dueFrom]: filters.dueFrom,
    [FILTER_PARAM.dueTo]: filters.dueTo,
    [FILTER_PARAM.hasDue]: filters.hasDueDate ? 1 : undefined,
    [FILTER_PARAM.risk]: filters.atRisk ? 1 : undefined,
    [FILTER_PARAM.overdue]: filters.overdue ? 1 : undefined,
    [FILTER_PARAM.search]: filters.search,
  };
}

/** Case-insensitive haystack of the fields a person would search by. */
export function projectSearchText(
  project: Project,
  peopleNames: Map<string, string>,
  streamsById: Map<string, string>,
): string {
  const assignees = project.assignments
    .map((a) => peopleNames.get(a.personId) ?? '')
    .filter(Boolean)
    .join(' ');
  return [project.title, project.description, streamsById.get(project.streamId) ?? '', assignees]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/** A multi-value dimension hits when ANY of its values does; an UNSET or EMPTY
 *  list hits everything (the popover's «Очистить» outcome = no filter). */
function anyOf(list: readonly string[] | undefined, hits: (value: string) => boolean): boolean {
  return list === undefined || list.length === 0 || list.some(hits);
}

/** The predicate every view applies. `undefined`/empty filters are ignored;
 *  a multi-value key matches when ANY of its values hits (OR within a key). */
export function matchesFilters(
  project: Project,
  filters: RegistryFilters,
  context: { peopleNames: Map<string, string>; peopleTeams: Map<string, string>; streamsById: Map<string, string>; today?: string },
): boolean {
  if (!anyOf(filters.status, (status) => status === project.status)) return false;
  if (!anyOf(filters.streamId, (streamId) => streamId === project.streamId)) return false;
  if (!anyOf(filters.personId, (personId) => project.assignments.some((a) => a.personId === personId))) return false;
  if (!anyOf(filters.priority, (priority) => priority === project.priority)) return false;
  if (!anyOf(filters.teamId, (teamId) => project.assignments.some((a) => context.peopleTeams.get(a.personId) === teamId))) return false;
  // Period on «Срок до»: inclusive on both ends, ISO strings compare as dates.
  // With ANY bound set a project needs a date at all — "no deadline" is not
  // "sometime inside the period".
  if (filters.dueFrom !== undefined || filters.dueTo !== undefined) {
    if (project.dueDate === null) return false;
    if (filters.dueFrom !== undefined && project.dueDate < filters.dueFrom) return false;
    if (filters.dueTo !== undefined && project.dueDate > filters.dueTo) return false;
  }
  // «Только со сроком» (F-28): a project without any dueDate is out whenever
  // the filter is on — nothing to schedule around.
  if (filters.hasDueDate && project.dueDate === null) return false;
  if (filters.atRisk && !project.atRisk) return false;
  if (filters.overdue && !isOverdue(project, context.today)) return false;
  if (filters.categoryId) {
    if ((project.categoryId ?? null) !== filters.categoryId) return false;
  }
  if (filters.search) {
    const haystack = projectSearchText(project, context.peopleNames, context.streamsById);
    // Every whitespace-separated word must be present, in any order.
    for (const token of filters.search.toLowerCase().split(/\s+/).filter(Boolean)) {
      if (!haystack.includes(token)) return false;
    }
  }
  return true;
}

/** Builds the lookup maps `matchesFilters` needs from the dictionaries. */
export function buildFilterContext(
  people: ReadonlyArray<{ id: string; name: string; teamId: string }>,
  streams: readonly Stream[],
  categories?: readonly Category[],
  today?: string,
): { peopleNames: Map<string, string>; peopleTeams: Map<string, string>; streamsById: Map<string, string>; categoriesById: Map<string, string>; today: string } {
  const byName = (list: ReadonlyArray<{ id: string; name: string }>) =>
    new Map(list.map((item) => [item.id, item.name]));
  return {
    peopleNames: byName(people),
    peopleTeams: new Map(people.map((person) => [person.id, person.teamId])),
    streamsById: byName(streams),
    categoriesById: byName(categories ?? []),
    today: today ?? todayIso(),
  };
}

/** True when at least one filter is set (drives the filtered-empty state).
 *  An EMPTY multi-value list counts as unset — no selection is no filter. */
export function hasActiveFilters(filters: RegistryFilters): boolean {
  return (
    (filters.status?.length ?? 0) > 0 ||
    (filters.streamId?.length ?? 0) > 0 ||
    (filters.personId?.length ?? 0) > 0 ||
    (filters.teamId?.length ?? 0) > 0 ||
    filters.categoryId !== undefined ||
    (filters.priority?.length ?? 0) > 0 ||
    filters.dueFrom !== undefined ||
    filters.dueTo !== undefined ||
    filters.hasDueDate === true ||
    filters.atRisk === true ||
    filters.overdue === true ||
    filters.search !== undefined
  );
}

// ---------------------------------------------------------------------------
// Sorting (queue №1 by default: priority rank → due date → title)
// ---------------------------------------------------------------------------

export type ProjectSortKey = 'priority' | 'dueDate' | 'title' | 'status';

export const SORT_LABELS: Record<ProjectSortKey, string> = {
  priority: 'приоритет',
  dueDate: 'срок',
  title: 'название',
  status: 'статус',
};

/**
 * Sort direction of a column header («Все проекты», F-19). Absent in the URL =
 * `asc` for EVERY key: priority rank asc is queue №1 («сначала срочные») and
 * dueDate asc is «сначала ближайшие», so `asc` is the honest default.
 */
export type SortDirection = 'asc' | 'desc';

/**
 * The keys the /projects table can sort by: the four shared keys (their
 * existing `?sort=` values keep their meaning) plus the table-only ones.
 * Stream and category rank by NAME (an id string would sort by its random
 * prefix), hours by number, `manual` by the global `allPosition` (F-28).
 * Non-columns (drag handle, at-risk marker, assignees, actions) are not
 * sortable.
 */
export type ProjectTableSortKey = ProjectSortKey | 'stream' | 'category' | 'hours' | 'manual';

/** Name lookups the name-ranked keys need; a missing map degrades to the raw id. */
export interface SortNameContext {
  streamsById: Map<string, string>;
  categoriesById: Map<string, string>;
}

/**
 * Sort rank of a status BY DICTIONARY ORDER (`registry.statuses`, F-01
 * 2026-10-14). Optional and injected by the caller (views have the registry,
 * this pure module does not); when absent/empty the seven built-ins in their
 * canonical order are the fallback — a status sorting never blocks on a
 * loading document. Unknown ids rank last, never first.
 */
export function statusRankLookup(statuses?: readonly RegistryStatus[]): (status: ProjectStatus) => number {
  const entries: Array<[string, number]> =
    statuses !== undefined && statuses.length > 0
      ? statuses.map((status, index) => [status.id, index])
      : PROJECT_STATUSES.map((id, index) => [id, index]);
  const rank = new Map(entries);
  const last = entries.length;
  return (status) => rank.get(status) ?? last;
}

/**
 * The shared `priorityRank` is total only for enum values: a document row with
 * an out-of-enum priority would give `indexOf → -1` and sort FIRST. Sorting
 * must degrade gracefully — unknown/absent is the lowest rank of all.
 */
function rankOf(priority: Priority | undefined): number {
  if (priority === undefined) return PROJECT_PRIORITIES.length;
  const rank = priorityRank(priority);
  return rank >= 0 ? rank : PROJECT_PRIORITIES.length;
}

/** Reference keys rank by NAME (`ru` collation); an absent reference is the
 *  lowest rank of all in asc — the same "unknown last" rule as `rankOf`. */
function nameTieBreak(
  a: string | null,
  b: string | null,
  namesById: Map<string, string> | undefined,
): number {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return (namesById?.get(a) ?? a).localeCompare(namesById?.get(b) ?? b, 'ru');
}

/** Hours: numbers ascending, unset (null) last — the dateless-dueDate rule. */
function hoursTieBreak(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

/**
 * The global manual order (F-28): `allPosition` asc, `null` last. Ties stay 0
 * ON PURPOSE — equal (or absent) positions mean "keep the array/document
 * order", which a stable `Array.prototype.sort` preserves; inventing a
 * tie-break here would rearrange lists nobody has reordered yet.
 */
function allPositionTieBreak(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

/**
 * `rank urgent<high<medium<low`, tie → dueDate asc → title (spec §1.3).
 *
 * `direction` (F-19, 2026-10-14) inverts the PRIMARY comparison only; the
 * deterministic tie-breaks under it stay ascending in BOTH directions (a desc
 * column still reads predictably inside equal-primary groups). Unset values
 * (dateless, null hours, no stream/category) sit at the END of an asc sort —
 * and, as the plain `-1` of that, at the FRONT of a desc one.
 *
 * `statusRank` (F-01, 2026-10-14) is the OPTIONAL registry-order lookup for
 * `key: 'status'` — dictionary order when given, built-in canonical order
 * otherwise. Deliberately the LAST parameter so call sites that do not care
 * stay untouched.
 *
 * `key: 'manual'` (F-28, 2026-10-23) is the one exception to `direction`: it
 * ranks by `allPosition` only and ignores the direction entirely (no desc).
 */
export function compareProjects(
  a: Project,
  b: Project,
  key: ProjectTableSortKey = 'priority',
  pickPriority: (project: Project) => Priority | undefined = (project) => project.priority,
  direction: SortDirection = 'asc',
  names?: Partial<SortNameContext>,
  statusRank: (status: ProjectStatus) => number = statusRankLookup(),
): number {
  const dir = direction === 'desc' ? -1 : 1;
  switch (key) {
    case 'title':
      return dir * a.title.localeCompare(b.title, 'ru') || priorityTieBreak(a, b, pickPriority);
    case 'status':
      return (
        dir * (statusRank(a.status) - statusRank(b.status)) ||
        priorityTieBreak(a, b, pickPriority) ||
        a.title.localeCompare(b.title, 'ru')
      );
    case 'dueDate':
      return (
        dir * dueDateTieBreak(a.dueDate, b.dueDate) ||
        priorityTieBreak(a, b, pickPriority) ||
        a.title.localeCompare(b.title, 'ru')
      );
    case 'stream':
      return (
        dir * nameTieBreak(a.streamId, b.streamId, names?.streamsById) ||
        priorityTieBreak(a, b, pickPriority) ||
        a.title.localeCompare(b.title, 'ru')
      );
    case 'category':
      return (
        dir * nameTieBreak(a.categoryId, b.categoryId, names?.categoriesById) ||
        priorityTieBreak(a, b, pickPriority) ||
        a.title.localeCompare(b.title, 'ru')
      );
    case 'hours':
      return (
        dir * hoursTieBreak(a.hours, b.hours) ||
        priorityTieBreak(a, b, pickPriority) ||
        a.title.localeCompare(b.title, 'ru')
      );
    case 'manual':
      // F-28: the global manual order — rank by `allPosition` and nothing
      // else. `direction` is IGNORED: manual has no desc (a reversed manual
      // order would make the row's «наверх» button a lie), the view cycles
      // the header click straight back to the default sort. Ties (0) keep the
      // document order via the stable sort.
      return allPositionTieBreak(a.allPosition, b.allPosition);
    case 'priority':
    default:
      return (
        dir * (rankOf(pickPriority(a)) - rankOf(pickPriority(b))) ||
        dueDateTieBreak(a.dueDate, b.dueDate) ||
        a.title.localeCompare(b.title, 'ru')
      );
  }
}

function priorityTieBreak(
  a: Project,
  b: Project,
  pickPriority: (project: Project) => Priority | undefined,
): number {
  return rankOf(pickPriority(a)) - rankOf(pickPriority(b));
}

function dueDateTieBreak(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a < b ? -1 : 1;
}

export function sortProjects(
  projects: readonly Project[],
  key: ProjectTableSortKey = 'priority',
  pickPriority?: (project: Project) => Priority | undefined,
  direction: SortDirection = 'asc',
  names?: Partial<SortNameContext>,
  statusRank?: (status: ProjectStatus) => number,
): Project[] {
  return [...projects].sort((a, b) => compareProjects(a, b, key, pickPriority, direction, names, statusRank));
}
